import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { IntervalType } from '@api-types/enums/interval-type.enum';
import { PmDialogComponent } from '@libs/ui';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { ForecastSeriesActions } from 'demand-forecasting/store/forecast-series/forecast-series.actions';
import { isForecastSeriesFetchingOrPending } from 'demand-forecasting/store/forecast-series/forecast-series.selectors';
import { ConfigurationRouterHelperService } from 'demand-forecasting/store/router-state/configuration-router-helper.service';
import { FixedYAxisZeroPointComponent } from './fixed-y-axis-zero-point.component';
import { IntervalsAndConfidenceLevelComponent } from './intervals-and-confidence-level.component';

@Component({
  selector: 'pm-chart-options-dialog',
  templateUrl: './chart-options-dialog.component.html',
  imports: [
    PmDialogComponent,
    IntervalsAndConfidenceLevelComponent,
    TranslateModule,
    FixedYAxisZeroPointComponent,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
  ],
})
export class ChartOptionsDialogComponent {
  private readonly store = inject(Store);
  private readonly dialogRef = inject(MatDialogRef);
  private readonly configurationRouterHelperService = inject(
    ConfigurationRouterHelperService,
  );

  protected isLoading = this.store.selectSignal(
    isForecastSeriesFetchingOrPending,
  );

  protected readonly primaryCtaButtonConfig = computed(() => ({
    text: 'UTILITY.APPLY_CHANGES',
    isLoading: this.isLoading(),
    handleClick: () => this.applyChanges(),
  }));

  fixedYAxisZeroPoint = signal(true);
  showInterval = signal(false);
  confidenceLevel = signal(0);
  intervalType = signal<IntervalType | undefined>(undefined);

  onFixedYAxisZeroPointChange(value: boolean): void {
    this.fixedYAxisZeroPoint.set(value);
  }

  onShowIntervalChange(value: boolean): void {
    this.showInterval.set(value);
  }

  onConfidenceLevelChange(value: number): void {
    this.confidenceLevel.set(value);
  }

  onIntervalTypeChange(value: IntervalType | undefined): void {
    this.intervalType.set(value);
  }

  applyChanges(): void {
    const showInterval = this.showInterval();
    const confidenceLevel = showInterval ? this.confidenceLevel() : null;
    const intervalType = showInterval ? (this.intervalType() ?? null) : null;

    this.configurationRouterHelperService.updateChartOptions(
      confidenceLevel,
      intervalType,
      String(this.fixedYAxisZeroPoint()),
    );

    this.store.dispatch(
      ForecastSeriesActions.applyForecastSeriesOptions({
        options: {
          intervalType,
          ...(confidenceLevel === null ? {} : { confidenceLevel }),
        },
      }),
    );

    this.dialogRef.close();
  }
}
